// The layout: the project's sheet with every tracked run drawn over it.
//
// SVG, not canvas. A run has to be hoverable, focusable and clickable
// individually, and the sheet is a static raster underneath - which is
// exactly what SVG over an <img> gives for free, without a hit-testing
// layer of our own.
//
// Geometry is stored as PERCENT of the sheet image (0-100 on each axis),
// exactly as coordinationArea.points is - so the overlay registers to the
// drawing at any zoom or pane size, and a re-rasterised sheet at a
// different resolution does not move a single run. The svg is
// viewBox="0 0 100 100" with preserveAspectRatio="none" laid over the
// <img>, which is the same trick the areas page uses; strokes carry
// vectorEffect="non-scaling-stroke" so the non-uniform scale cannot
// squash them.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Select, DatePicker, Button, Empty, Checkbox, Tooltip, Input, Popover, Tag, message } from "antd";
import moment from "moment";
import useSheetImage from "./useSheetImage";
import { PlusOutlined, UndoOutlined, CloseOutlined } from "@ant-design/icons";
import SheetStage, { pointAt as stagePointAt } from "./SheetStage";
import { buildVersion } from "helpers/buildVersion";
import {
  STATUS_ORDER, STATUS_COLOR, STATUS_SHORT, STATUS_LABEL, STATUS_DASH,
  systemColor, systemLabel, sheetLabel,
} from "./trackingTheme";

// A palette to pick an item's own colour from, rather than a free colour
// input: eight hand-picked hues stay distinguishable from each other on a
// busy drawing, and an arbitrary picker produces the pale yellows and
// near-whites that vanish against a sheet.
const ITEM_COLORS = [
  "#2a78d6", "#eb6834", "#1baf7a", "#eda100",
  "#e87ba4", "#008300", "#4a3aa7", "#e34948",
];

const T_LINE = "#EFEFEF";


// The status a whole run shows on the drawing: the LEAST advanced of its
// segments. A run with one span still projected is not an installed run,
// and colouring it as one would make the drawing lie at a glance - which
// is the only way anybody reads a drawing.
function rollup(item) {
  const segs = item.segments || [];
  if (!segs.length) return "projected";
  let worst = "installed";
  for (const s of segs) {
    const i = STATUS_ORDER.indexOf(s.status);
    if (i >= 0 && i < STATUS_ORDER.indexOf(worst)) worst = s.status;
  }
  return worst;
}

// What the field edits without leaving the drawing. Status and date are
// one control, deliberately: a status with no date drops out of every
// timeline chart on the page, so the server refuses one without the
// other and the UI should not offer it either.
function SegmentPopup({ item, segment, canEdit, onSet, onClose }) {
  const [status, setStatus] = useState(segment.status || "projected");
  const [date, setDate] = useState(segment.statusDate ? moment(segment.statusDate) : moment());
  const [saving, setSaving] = useState(false);
  const dirty = status !== segment.status
    || (segment.statusDate ? !moment(segment.statusDate).isSame(date, "day") : true);

  return (
    <div
      style={{
        background: "#fff", border: "1px solid #E2E2E2", borderRadius: 6,
        boxShadow: "0 4px 16px rgba(35,52,62,.16)", padding: 12, minWidth: 260,
      }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div style={{ fontSize: 12.5, fontWeight: 700, color: "#23343E", marginBottom: 2 }}>
        {item.mark || item.name || "Item"} · {systemLabel(item.system)}
      </div>
      <div style={{ fontSize: 11, color: "#8D8D8D", marginBottom: 8 }}>
        {segment.label || segment.key}
        {segment.length ? ` · ${segment.length} ft` : ""}
        {item.size ? ` · ${item.size}` : ""}
      </div>

      {STATUS_ORDER.map((s) => (
        <label key={s} style={{ display: "flex", alignItems: "center", gap: 8, padding: "3px 0", cursor: canEdit ? "pointer" : "default" }}>
          <input type="radio" name={`st-${item._id}-${segment.key}`} value={s}
            checked={status === s} disabled={!canEdit}
            onChange={() => setStatus(s)} />
          <span style={{ width: 10, height: 10, borderRadius: 2, background: STATUS_COLOR[s], flexShrink: 0 }} />
          <span style={{ fontSize: 12, color: "#3A4952" }}>{STATUS_LABEL[s]}</span>
        </label>
      ))}

      <div style={{ marginTop: 8 }}>
        <div style={{ fontSize: 10.5, color: "#8D8D8D", marginBottom: 3 }}>
          {status === "projected" ? "Expected on" : "Reached on"}
        </div>
        <DatePicker
          size="small" style={{ width: "100%" }} value={date} allowClear={false}
          disabled={!canEdit} onChange={(d) => setDate(d || moment())}
        />
      </div>

      <div style={{ display: "flex", gap: 8, marginTop: 10, justifyContent: "flex-end" }}>
        <Button size="small" onClick={onClose}>Close</Button>
        {canEdit && (
          <Button
            size="small" type="primary" loading={saving} disabled={!dirty}
            onClick={async () => {
              setSaving(true);
              try { await onSet({ status, statusDate: date.format("YYYY-MM-DD") }); onClose(); }
              finally { setSaving(false); }
            }}
          >
            Save
          </Button>
        )}
      </div>
    </div>
  );
}

export default function TrackingLayout({
  // `items` are the DRAWN ones - every item with geometry on a sheet -
  // not the table's page: the overlay must not change when the table
  // turns a page. `total` and `unplaced` are counted on the server over
  // the whole board.
  items, total = null, unplaced = 0, sheets, grids, canEdit, onSetSegment, onCreateItem,
  // Adding an item is its own key (2026-10-14); absent = canEdit, as before.
  canAdd = undefined,
  visibleSystems, onToggleSystem, sheetId, onSheetChange, shownView = true,
}) {
  const [hover, setHover] = useState(null);      // { item, segment, x, y }
  const [pinned, setPinned] = useState(null);
  const svgRef = useRef(null);

  // DRAWING A NEW ITEM, on the drawing, where the thing actually is.
  //
  // A run is drawn span by span: each finished polyline becomes one
  // SEGMENT of the item, which is what makes the run's percentage mean
  // something later. Finish the item when the last span is in.
  const [drawing, setDrawing] = useState(false);
  const [draft, setDraft] = useState([]);        // the span being drawn
  const [spans, setSpans] = useState([]);        // finished spans of this item
  const [form, setForm] = useState({ mark: "", system: "cableTray", size: "", kind: "run", color: ITEM_COLORS[0] });
  const [saving, setSaving] = useState(false);

  const pointAt = (e) => stagePointAt(svgRef.current, e);
  const endSpan = () => {
    const need = form.kind === "gear" ? 1 : 2;
    if (draft.length < need) return;
    setSpans((sp) => [...sp, draft]);
    setDraft([]);
  };
  const resetDraw = () => { setDrawing(false); setDraft([]); setSpans([]); setForm((f) => ({ ...f, mark: "", size: "" })); };

  const saveItem = async () => {
    const all = draft.length >= (form.kind === "gear" ? 1 : 2) ? [...spans, draft] : spans;
    if (!all.length) { message.warning("Draw at least one span"); return; }
    setSaving(true);
    try {
      await onCreateItem({
        mark: form.mark.trim(), system: form.system, size: form.size.trim(),
        kind: form.kind, color: form.color,
        originVersion: buildVersion(),
        sheetDocControlId: sheetId,
        points: all.flat(),
        // Each span is a segment, so the run can be part-installed - the
        // whole reason segments exist.
        segments: all.map((pts, i) => ({
          key: `s${i + 1}`,
          label: `span ${i + 1}`,
          length: 0,
          status: "projected",
          statusDate: null,
          points: pts,
        })),
      });
      message.success(`${form.mark.trim() || systemLabel(form.system)} added — ${all.length} span${all.length === 1 ? "" : "s"}`);
      resetDraw();
    } catch (e) {
      message.error(e.message || "Could not add the item");
    } finally { setSaving(false); }
  };

  const sheet = sheets.find((s) => String(s._id) === String(sheetId)) || sheets[0] || null;
  // Mounted but hidden under the model view: the drawing is rasterised
  // only once this view has been shown (owner, 2026-09-18: the sheet is
  // not loaded every time on the model tracking).
  const [shown, setShown] = useState(shownView);
  useEffect(() => { if (shownView) setShown(true); }, [shownView]);
  const sheetImg = useSheetImage(shown ? sheet : null);
  const onSheet = useMemo(
    () => items.filter((i) => (!sheet || String(i.sheetDocControlId) === String(sheet._id))
      && (i.points || []).length
      && visibleSystems.includes(i.system || "other")),
    [items, sheet, visibleSystems]
  );


  // Items the drawing CANNOT show, because nothing has given them a
  // position on a sheet.
  //
  // A model import is the usual way to get them: it brings categories,
  // sizes and levels but no geometry, since a Revit element knows where
  // it is in the building and nothing in the export says where the
  // drawing is. Those items used to be filtered out of the overlay
  // silently, which reads as "I imported the model and cannot see it".
  // An absence that is reported is a state; an absence that is not is a
  // bug report.
  const systemsPresent = useMemo(() => {
    const set = new Set([...(visibleSystems || []), ...items.map((i) => i.system || "other")]);
    return [...set];
  }, [items, visibleSystems]);

  const active = pinned || hover;

  // An empty board still renders the pane: "Add tracking item" is how a
  // project with nothing on it gets its first run, and hiding the sheet
  // behind an empty state would hide the only way to start.
  if (!items.length && !unplaced && !(total > 0) && !drawing) {
    return (
      <div style={{ background: "#fff", border: "1px solid #EFEFEF", borderRadius: 6, padding: 40, textAlign: "center" }}>
        <Empty description="Nothing tracked on this project yet — draw an item on a sheet, or add a schedule / layout / model source." />
        {(canAdd === undefined ? canEdit : canAdd) && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setDrawing(true)} disabled={!sheets.length}>
            Add tracking item
          </Button>
        )}
      </div>
    );
  }

  return (
    <div style={{ background: "#fff", border: "1px solid #EFEFEF", borderRadius: 6, display: "flex", flexDirection: "column", minHeight: 0 }}>
      {/* System toggles: the overlay's own legend, and its filter. Not a
          page filter - these decide what is DRAWN, not what is counted. */}
      <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap", padding: "8px 12px", borderBottom: "1px solid #EFEFEF" }}>
        {sheets.length > 1 && (
          <Select
            size="small" style={{ minWidth: 200 }} value={sheet ? String(sheet._id) : undefined}
            onChange={onSheetChange}
            options={sheets.map((s) => ({ value: String(s._id), label: sheetLabel(s) }))}
          />
        )}
        {systemsPresent.map((s) => (
          <Checkbox
            key={s}
            checked={visibleSystems.includes(s)}
            onChange={() => onToggleSystem(s)}
          >
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <span style={{ width: 10, height: 10, borderRadius: 2, background: systemColor(s) }} />
              <span style={{ fontSize: 12 }}>{systemLabel(s)}</span>
            </span>
          </Checkbox>
        ))}
        <span style={{ flex: 1 }} />
        {(canAdd === undefined ? canEdit : canAdd) && !drawing && (
          <Button size="small" type="primary" icon={<PlusOutlined />} onClick={() => setDrawing(true)}>
            Add tracking item
          </Button>
        )}
        {!drawing && (
          <span style={{ fontSize: 10.5, color: "#8D8D8D" }}>
            Hover a run for its status · click to pin
            {(grids || []).length ? ` · ${grids.length} grid lines known` : ""}
          </span>
        )}
        {!drawing && unplaced > 0 && (
          <Tooltip title="These came from a model or a schedule, which say what something is but not where the drawing puts it. Switch to the Model view to place them over this sheet, or trace them here.">
            <Tag color="orange" style={{ marginInlineEnd: 0, cursor: "help" }}>
              {unplaced} not placed on a sheet
            </Tag>
          </Tooltip>
        )}
      </div>

      {drawing && (
        <div style={{
          display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
          padding: "8px 12px", borderBottom: `1px solid ${T_LINE}`, background: "#FBFDFF",
        }}>
          <Input size="small" style={{ width: 110 }} placeholder="Mark"
                 value={form.mark} onChange={(e) => setForm({ ...form, mark: e.target.value })} />
          <Select size="small" style={{ width: 130 }} value={form.system}
                  onChange={(v) => setForm({ ...form, system: v })}
                  options={["cableTray", "conduit", "busDuct", "gear", "duct", "pipe"].map((v) => ({ value: v, label: systemLabel(v) }))} />
          <Select size="small" style={{ width: 96 }} value={form.kind}
                  onChange={(v) => { setForm({ ...form, kind: v }); setDraft([]); }}
                  options={[{ value: "run", label: "Run" }, { value: "gear", label: "Grid point" }]} />
          <Input size="small" style={{ width: 84 }} placeholder="Size"
                 value={form.size} onChange={(e) => setForm({ ...form, size: e.target.value })} />
          <Popover
            trigger="click"
            content={(
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 26px)", gap: 6 }}>
                {ITEM_COLORS.map((c) => (
                  <button key={c} type="button" onClick={() => setForm({ ...form, color: c })}
                    style={{
                      width: 26, height: 26, borderRadius: 4, background: c, cursor: "pointer",
                      border: form.color === c ? "2px solid #23343E" : "1px solid #E2E2E2",
                    }} />
                ))}
              </div>
            )}
          >
            <Button size="small">
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <span style={{ width: 12, height: 12, borderRadius: 3, background: form.color }} />
                Colour
              </span>
            </Button>
          </Popover>
          <Button size="small" onClick={endSpan} disabled={draft.length < (form.kind === "gear" ? 1 : 2)}>
            End span ({spans.length})
          </Button>
          <Button size="small" icon={<UndoOutlined />} onClick={() => setDraft((d) => d.slice(0, -1))} disabled={!draft.length}>
            Undo point
          </Button>
          <span style={{ flex: 1 }} />
          <Button size="small" type="primary" loading={saving} onClick={saveItem}>
            Save item
          </Button>
          <Button size="small" icon={<CloseOutlined />} onClick={resetDraw}>Cancel</Button>
          <div style={{ width: "100%", fontSize: 10.5, color: "#8D8D8D" }}>
            Click along the run · double-click ends a span · each span becomes a segment you can
            mark procured / installing / installed on its own. Everything starts as projected.
          </div>
        </div>
      )}

      {/* SheetStage keeps the svg exactly over the image - see the file
          for why that is not something to hand-roll per pane. */}
      <SheetStage
        img={sheetImg}
        svgRef={svgRef}
        cursor={drawing ? "crosshair" : "default"}
        outerStyle={{ flex: 1, minHeight: 420 }}
        onMouseDown={() => { if (!drawing) setPinned(null); }}
        onClick={(e) => {
          if (!drawing) return;
          const p = pointAt(e);
          if (form.kind === "gear") { setDraft([p]); return; }
          setDraft((d) => [...d, p]);
        }}
        onDoubleClick={() => { if (drawing) endSpan(); }}
      >
          {/* The sidecar's grids are in MODEL units and only become sheet
              positions through a viewport transform, which no plugin
              export has emitted yet (sheetViewport is empty everywhere).
              Drawing them as if they were sheet percentages would put
              confident lines in the wrong places, so until that transform
              exists they are counted, not drawn. */}

          {onSheet.map((item) => {
            const segs = (item.segments || []).filter((s) => (s.points || []).length >= 2);
            // Screen pixels, not user units: non-scaling-stroke means the
            // non-uniform viewBox cannot stretch a run into a ribbon.
            const stroke = 3;
            // Segments carry their own geometry where a source supplied
            // it, so a half-installed run is drawn half installed. Where
            // they do not, the run is one stroke in its rollup status -
            // honest about what is known rather than inventing spans.
            const parts = segs.length
              ? segs
              : [{ key: "_all", points: item.points, status: rollup(item), length: 0, label: "whole run" }];
            return parts.map((seg) => {
              const pts = (seg.points || []).map((p) => `${p.x},${p.y}`).join(" ");
              const isGear = item.kind === "gear" || (seg.points || []).length === 1;
              const on = active && active.item._id === item._id && active.segment.key === seg.key;
              const colour = STATUS_COLOR[seg.status] || STATUS_COLOR.projected;
              const enter = (e) => setHover({ item, segment: seg, x: e.clientX, y: e.clientY });
              if (isGear) {
                const p = (seg.points || item.points)[0] || { x: 0, y: 0 };
                return (
                  <g key={`${item._id}-${seg.key}`} onMouseEnter={enter} onMouseLeave={() => setHover(null)}
                     onMouseDown={(e) => { e.stopPropagation(); setPinned({ item, segment: seg, x: e.clientX, y: e.clientY }); }}
                     style={{ cursor: "pointer" }}>
                    {/* A generous invisible hit area under a small mark -
                        a marker you have to land on dead-centre is not a
                        control anybody can use on site. */}
                    <circle cx={p.x} cy={p.y} r={1.6} fill="transparent" />
                    <circle cx={p.x} cy={p.y} r={on ? 1.1 : 0.9} fill={colour}
                            stroke="#fff" strokeWidth={2} vectorEffect="non-scaling-stroke" />
                  </g>
                );
              }
              return (
                <g key={`${item._id}-${seg.key}`} onMouseEnter={enter} onMouseLeave={() => setHover(null)}
                   onMouseDown={(e) => { e.stopPropagation(); setPinned({ item, segment: seg, x: e.clientX, y: e.clientY }); }}
                   style={{ cursor: "pointer" }}>
                  {/* A fat invisible line first: the hit area has to be
                      bigger than the mark, or a 3px run is unclickable on
                      site. */}
                  <polyline points={pts} fill="none" stroke="transparent" strokeWidth={stroke * 5}
                            strokeLinecap="round" vectorEffect="non-scaling-stroke" />
                  {/* A 2px surface ring, not a border: it separates a run
                      from the one crossing under it without drawing a box
                      around either. */}
                  <polyline points={pts} fill="none" stroke="#fff" strokeWidth={stroke + 4}
                            strokeLinecap="round" vectorEffect="non-scaling-stroke" />
                  {/* The ITEM's own colour as a halo under the run - it
                      says WHICH run this is. The run itself keeps its
                      STATUS colour on top, because how far along it is
                      is the question the drawing exists to answer. */}
                  {item.color && (
                    <polyline points={pts} fill="none" stroke={item.color} strokeWidth={stroke + 3}
                              strokeOpacity={0.45} strokeLinecap="round" vectorEffect="non-scaling-stroke" />
                  )}
                  <polyline points={pts} fill="none" stroke={colour}
                            strokeWidth={on ? stroke + 2 : stroke}
                            strokeDasharray={STATUS_DASH[seg.status] || undefined}
                            strokeLinecap={STATUS_DASH[seg.status] ? "butt" : "round"}
                            vectorEffect="non-scaling-stroke" />
                </g>
              );
            });
          })}

          {/* What is being drawn right now, in the item's own colour. */}
          {drawing && (
            <g pointerEvents="none">
              {spans.map((sp, i) => (sp.length === 1
                ? <circle key={`sp${i}`} cx={sp[0].x} cy={sp[0].y} r={0.9} fill={form.color}
                          stroke="#fff" strokeWidth={2} vectorEffect="non-scaling-stroke" />
                : <polyline key={`sp${i}`} points={sp.map((p) => `${p.x},${p.y}`).join(" ")} fill="none"
                            stroke={form.color} strokeWidth={4} strokeLinecap="round" vectorEffect="non-scaling-stroke" />))}
              {draft.length > 0 && (form.kind === "gear"
                ? <circle cx={draft[0].x} cy={draft[0].y} r={0.9} fill={form.color}
                          stroke="#fff" strokeWidth={2} vectorEffect="non-scaling-stroke" />
                : <polyline points={draft.map((p) => `${p.x},${p.y}`).join(" ")} fill="none"
                            stroke={form.color} strokeWidth={4} strokeDasharray="7 5"
                            strokeLinecap="round" vectorEffect="non-scaling-stroke" />)}
              {draft.map((p, i) => (
                <circle key={`d${i}`} cx={p.x} cy={p.y} r={0.55} fill="#fff"
                        stroke={form.color} strokeWidth={2} vectorEffect="non-scaling-stroke" />
              ))}
            </g>
          )}
      </SheetStage>

      {/* The hover popup would fight the drawing for every click. Fixed
          to the viewport, so it lives outside the stage. */}
      {!drawing && active && (
        <div style={{
          position: "fixed", zIndex: 1100,
          left: Math.min(active.x + 14, (typeof window !== "undefined" ? window.innerWidth : 1200) - 290),
          top: Math.min(active.y + 14, (typeof window !== "undefined" ? window.innerHeight : 800) - 320),
        }}>
          <SegmentPopup
            item={active.item} segment={active.segment} canEdit={canEdit}
            onSet={(body) => onSetSegment(active.item, active.segment, body)}
            onClose={() => { setPinned(null); setHover(null); }}
          />
        </div>
      )}

      {/* The ramp reads light -> dark in install order, so it is a scale,
          not a key of unrelated colours. */}
      <div style={{ display: "flex", alignItems: "center", gap: 14, padding: "8px 12px", borderTop: "1px solid #EFEFEF", flexWrap: "wrap" }}>
        {STATUS_ORDER.map((s) => (
          <span key={s} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            {/* The swatch shows the LINE, dash and all - a legend that
                explains the colour and hides the pattern explains half
                the encoding. */}
            <svg width="26" height="8" style={{ display: "block" }}>
              <line x1="0" y1="4" x2="26" y2="4" stroke={STATUS_COLOR[s]} strokeWidth="3"
                    strokeDasharray={STATUS_DASH[s] || undefined} />
            </svg>
            <span style={{ fontSize: 11, color: "#3A4952" }}>{STATUS_SHORT[s]}</span>
          </span>
        ))}
        <span style={{ flex: 1 }} />
        <Tooltip title="Runs with no geometry yet are in the table but not on the drawing — they arrive here once a layout source is matched in.">
          <span style={{ fontSize: 10.5, color: "#8D8D8D" }}>
            {onSheet.length} of {(total === null ? items.length : total).toLocaleString()} drawn
          </span>
        </Tooltip>
      </div>
    </div>
  );
}
